import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { HotKeyConstant } from '../constants';
import { focusManager } from '../FocusManager';
import { hotKeysManager } from '../HotKeysManager';
import type { HotKeyRegistration } from '../types';
import { getCrossPlatformKeys } from '../utils/crossPlatform';
import { useFocusManager } from './useFocusManager';

export interface UseHotKeysManagerOptions {
    // componentId используется для связи useFocusManager и useHotKey
    // для правильного определения хэндлера при смене активного элемента
    componentId?: string;
    // handlerName используется только для дебага
    handlerName?: string;

    enabled?: boolean | (() => boolean);
    // статическая проверка доступности хэндлера, которая не может быть добавлена в enabled
    staticAvailabilityCheck?: () => boolean;
    // repeat используется когда нужно вызывать callback повторно при долгом нажатии клавиши
    repeat?: boolean;
    keyUp?: boolean;

    preventDefault?: boolean;
    stopPropagation?: boolean;
    stopImmediatePropagation?: boolean;
}

export interface UseHotKeysManagerReturn<T extends HTMLElement = HTMLElement> {
    ref: React.RefObject<T | null>;
}

// разные вариантов использования useHotKey
export function useHotKey<T extends HTMLElement = HTMLElement>(
    keys: HotKeyConstant,
    callback: ((event: KeyboardEvent) => void) | undefined,
    deps: React.DependencyList,
): UseHotKeysManagerReturn<T>;

export function useHotKey<T extends HTMLElement = HTMLElement>(
    keys: HotKeyConstant,
    callback: ((event: KeyboardEvent) => void) | undefined,
    options: UseHotKeysManagerOptions,
    deps: React.DependencyList,
): UseHotKeysManagerReturn<T>;

export function useHotKey<T extends HTMLElement = HTMLElement>(
    keys: HotKeyConstant,
    callback: ((event: KeyboardEvent) => void) | undefined,
    options: UseHotKeysManagerOptions,
): UseHotKeysManagerReturn<T>;

export function useHotKey<T extends HTMLElement = HTMLElement>(
    keys: HotKeyConstant,
    callback: ((event: KeyboardEvent) => void) | undefined,
): UseHotKeysManagerReturn<T>;

export function useHotKey<T extends HTMLElement = HTMLElement>(
    keys: HotKeyConstant,
    callback: ((event: KeyboardEvent) => void) | undefined,
    optionsOrDeps?: UseHotKeysManagerOptions | React.DependencyList,
    maybeDeps?: React.DependencyList,
): UseHotKeysManagerReturn<T> {
    // определяем что есть 3ий параметр, deps || options
    const isOptionsObject = optionsOrDeps !== undefined && !Array.isArray(optionsOrDeps);
    const options: UseHotKeysManagerOptions = isOptionsObject ? (optionsOrDeps as UseHotKeysManagerOptions) : {};
    const deps: React.DependencyList = isOptionsObject
        ? maybeDeps ?? []
        : (optionsOrDeps as React.DependencyList) ?? [];

    const { enabled = true, ...otherOptions } = options;

    // генерируем componentId один раз и сохраняем между ререндерами
    const componentId = useMemo(() => options.componentId ?? uuidv4(), [options.componentId]);
    // Преобразуем комбинации в кроссплатформенный формат
    const crossPlatformKeys: string[] = getCrossPlatformKeys(keys);

    const memoizedCallback = useCallback(callback || (() => {}), deps);
    const registrationRefs = useRef<HotKeyRegistration[] | null>(null);

    const isHandlerEnabled = typeof enabled === 'function' ? enabled() : enabled;

    const { ref } = useFocusManager<T>({
        componentId,
        enabled: isHandlerEnabled,
    });

    useEffect(() => {
        hotKeysManager.initialize();

        return () => {
            if (registrationRefs.current) {
                registrationRefs.current?.forEach(reg => reg.unregister());
                registrationRefs.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (isHandlerEnabled && !registrationRefs.current) {
            registrationRefs.current = crossPlatformKeys.map(key =>
                hotKeysManager.register(componentId, {
                    keys: key,
                    callback: memoizedCallback,
                    ...otherOptions,
                }),
            );
        }
        if (!isHandlerEnabled && registrationRefs.current) {
            registrationRefs.current?.forEach(reg => reg.unregister());
            registrationRefs.current = null;
        }
        // эффект для добавления / удаления хэндлера только при изменении enabled
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHandlerEnabled]);

    useEffect(() => {
        if (registrationRefs.current) {
            registrationRefs.current?.forEach(reg => reg.updateCallback(memoizedCallback));
        }
    }, [memoizedCallback]);

    useEffect(
        () => () => {
            focusManager.unregister(componentId);
        },
        [componentId],
    );

    return {
        ref,
    };
}
